import React, { useMemo } from "react";

import { copy } from "shared/assets";
import { FullScreen, Menu } from "shared/assets/icons";
import { ActionDropdown, IActionDropdownItem, IconButton } from "shared/components";
import { IImageItem, IImageStylePreset, ITheme } from "shared/types";
import { getImageURLWithStyle } from "shared/utils";

import { MutedText } from "shared/styles";
import { VisuallyHiddenElement } from "shared/styles/constants";
import {
  StyledFloatingButtonContainer,
  StyledImageTeaser,
  StyledImageTeaserOverlay,
} from "./styles";

export interface ImageTeaserProps extends IImageItem {
  onClick?: () => void;
  actions?: IActionDropdownItem[];
  nbMoreitems?: number;
}

const { imageForm } = copy;

export const ImageTeaser: React.FC<ImageTeaserProps> = ({
  imageStyles,
  onClick,
  description,
  nbMoreitems,
  actions,
}) => {
  const url = getImageURLWithStyle({
    imageStyles,
    style: IImageStylePreset.galleryThumbnail,
  });

  const actionSelectProps = useMemo(
    () => ({
      label: copy.menu.open,
      options: actions || [],
      triggerIcon: Menu,
      dataTestId: "teaser_action_select",
      wrapOptions: true,
    }),
    [actions]
  );

  const noPreview = useMemo(() => {
    const lowerCaseURL = url.toLowerCase();
    const fileType =
      lowerCaseURL.endsWith(".gif") ||
      lowerCaseURL.endsWith(".heic") ||
      lowerCaseURL.endsWith(".heif");

    return !!fileType;
  }, [url]);

  const teaserProps = useMemo(
    () => ({
      style: {
        backgroundImage: `url(${url})`,
      },
      "data-testid": "image_teaser",
    }),
    [url]
  );

  if (actions?.length) {
    return (
      <StyledImageTeaser {...teaserProps}>
        <VisuallyHiddenElement>{description}</VisuallyHiddenElement>
        {!!actions?.length && (
          <StyledFloatingButtonContainer>
            <ActionDropdown {...actionSelectProps} placement="bottom-end" theme={ITheme.primary} />
          </StyledFloatingButtonContainer>
        )}
        {noPreview && (
          <MutedText
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              WebkitTransform: "translate(-50%, -50%)",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            {imageForm.validation.heicError}
          </MutedText>
        )}
      </StyledImageTeaser>
    );
  }

  return (
    <StyledImageTeaser {...teaserProps} as="button" type="button" onClick={onClick}>
      {nbMoreitems ? (
        <StyledImageTeaserOverlay data-testid="image_teaser_more_overlay">
          {copy.imageTeaser.moreItems.replace("%s", nbMoreitems.toString()).toString()}
        </StyledImageTeaserOverlay>
      ) : (
        <StyledFloatingButtonContainer>
          <IconButton label={copy.imageGallery.openFullscreen} icon={FullScreen} size="m" />
        </StyledFloatingButtonContainer>
      )}
    </StyledImageTeaser>
  );
};
